import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { EnvironmentService } from '@services/enviroment/environment.service';
import {
  RoutineAggregatesResponse,
  RoutineQueueStatusResponse,
  RoutineLatestResponse,
  RoutineExecutionDetailResponse,
  PatientAggregateListResponse,
  PatientAggregateDetail,
  AggregateRefreshResponse,
  PipelineStatsSummary,
} from '@interfaces/backoffice';

@Injectable({
  providedIn: 'root',
})
export class BackofficeService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(EnvironmentService);
  private readonly apiUrl = this.env.getValue('apiUrl');

  // ---- Routines ----

  routineQueueStatus(): Observable<RoutineQueueStatusResponse> {
    return this.http.get<RoutineQueueStatusResponse>(
      `${this.apiUrl}/backoffice/api/routines/queue-status`
    );
  }

  routineAggregates(limit = 50, status?: string): Observable<RoutineAggregatesResponse> {
    let params = new HttpParams().set('limit', limit.toString());
    if (status) params = params.set('status', status);
    return this.http.get<RoutineAggregatesResponse>(
      `${this.apiUrl}/backoffice/api/routines/aggregates`,
      { params }
    );
  }

  routineLatest(patientId: string): Observable<RoutineLatestResponse> {
    return this.http.get<RoutineLatestResponse>(
      `${this.apiUrl}/backoffice/api/routines/patients/${patientId}/latest`
    );
  }

  routineExecutionDetail(patientId: string): Observable<RoutineExecutionDetailResponse> {
    return this.http.get<RoutineExecutionDetailResponse>(
      `${this.apiUrl}/backoffice/api/routines/patients/${patientId}/execution-detail`
    );
  }

  routineEnqueue(patientId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/backoffice/api/routines/enqueue/${patientId}`,
      null
    );
  }

  routineRequeue(patientId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/backoffice/api/routines/requeue/${patientId}`,
      null
    );
  }

  routineStop(patientId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/backoffice/api/routines/stop/${patientId}`,
      null
    );
  }

  routineProcessQueue(limit = 10): Observable<{ message: string; results: unknown }> {
    const params = new HttpParams().set('limit', limit.toString());
    return this.http.post<{ message: string; results: unknown }>(
      `${this.apiUrl}/backoffice/api/routines/process-queue`,
      null,
      { params }
    );
  }

  routineQueueReady(): Observable<{ message: string; results: unknown }> {
    return this.http.post<{ message: string; results: unknown }>(
      `${this.apiUrl}/backoffice/api/routines/queue-ready`,
      null
    );
  }

  // ---- Aggregates (admin management) ----

  aggregatesList(params: { limit?: number; offset?: number; has_survey?: boolean; has_doctor_notes?: boolean; has_microbiome?: boolean; is_ready?: boolean } = {}): Observable<PatientAggregateListResponse> {
    let httpParams = new HttpParams();
    if (params.limit != null) httpParams = httpParams.set('limit', params.limit.toString());
    if (params.offset != null) httpParams = httpParams.set('offset', params.offset.toString());
    if (params.has_survey != null) httpParams = httpParams.set('has_survey', params.has_survey.toString());
    if (params.has_doctor_notes != null) httpParams = httpParams.set('has_doctor_notes', params.has_doctor_notes.toString());
    if (params.has_microbiome != null) httpParams = httpParams.set('has_microbiome', params.has_microbiome.toString());
    if (params.is_ready != null) httpParams = httpParams.set('is_ready', params.is_ready.toString());
    return this.http.get<PatientAggregateListResponse>(
      `${this.apiUrl}/backoffice/api/aggregates`,
      { params: httpParams }
    );
  }

  aggregateDetail(patientId: string): Observable<PatientAggregateDetail> {
    return this.http.get<PatientAggregateDetail>(
      `${this.apiUrl}/backoffice/api/aggregates/${patientId}`
    );
  }

  aggregateRefresh(patientId: string): Observable<AggregateRefreshResponse> {
    return this.http.post<AggregateRefreshResponse>(
      `${this.apiUrl}/backoffice/api/aggregates/${patientId}/refresh`,
      null
    );
  }

  // ---- Stats ----

  statsSummary(): Observable<PipelineStatsSummary> {
    return this.http.get<PipelineStatsSummary>(
      `${this.apiUrl}/backoffice/api/stats/summary`
    );
  }
}
